import { applyAuthHeaders, buildAuthHeaders } from "@/lib/authHeaders"
import axios, { AxiosInstance, AxiosRequestConfig } from "axios"
import type {
  PatientCurrentState,
  PatientAPCMStateHistory,
  LifecycleSummary,
  PendingCarePlan,
  WorkflowStatus,
  BulkStateUpdateRequest,
  BulkUpdateResult,
  PreflightCheck,
  BulkInitResult,
  BulkIngestionJob,
  BulkIngestionJobSummary,
} from "../types/apcm-ops.types"
import type { RunSummary } from "../types/outreach-program.types"

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
  headers: { "Content-Type": "application/json" },
})

axiosInstance.interceptors.request.use(async (config) => {
  await applyAuthHeaders(config)
  return config
})

export async function apiCall<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await axiosInstance(config)
  return response.data
}

// ─── Read endpoints ──────────────────────────────────────────────

export function getAllStates(orgId: string) {
  return apiCall<PatientCurrentState[]>({
    method: "GET",
    url: `/patient-apcm-state/organization/${orgId}/all-states`,
  })
}

export function getPatientHistory(patientId: string, orgId: string) {
  return apiCall<PatientAPCMStateHistory>({
    method: "GET",
    url: `/patient-apcm-state/${patientId}/history`,
    params: { organization_id: orgId },
  })
}

export function getPatientCurrentState(patientId: string, orgId: string) {
  return apiCall<PatientCurrentState>({
    method: "GET",
    url: `/patient-apcm-state/${patientId}/current`,
    params: { organization_id: orgId },
  })
}

export function getLifecycleSummary(orgId: string) {
  return apiCall<LifecycleSummary>({
    method: "GET",
    url: `/apcm-workflow/patient-lifecycle-summary`,
    params: { organization_id: orgId },
  })
}

export function getPendingCarePlans(orgId: string) {
  return apiCall<PendingCarePlan[]>({
    method: "GET",
    url: `/apcm-workflow/care-plans/pending`,
    params: { organization_id: orgId },
  })
}

export async function downloadCarePlanPdf(
  patientId: string,
  carePlanId: string,
  orgId: string,
) {
  const response = await axiosInstance({
    method: "GET",
    url: `/patient/${patientId}/care-plans/${carePlanId}/download-pdf`,
    params: { organization_id: orgId },
    responseType: "blob",
  })
  const blob = new Blob([response.data], { type: "application/pdf" })
  const url = URL.createObjectURL(blob)
  window.open(url, "_blank")
}

export function getWorkflowStatus(workflowId: string) {
  return apiCall<WorkflowStatus>({
    method: "GET",
    url: `/apcm-workflow/workflow/${workflowId}/status`,
  })
}

export function getActiveWorkflow(patientId: string, orgId: string) {
  return apiCall<WorkflowStatus | null>({
    method: "GET",
    url: `/apcm-workflow/patient/${patientId}/active`,
    params: { organization_id: orgId },
  })
}

// ─── Action endpoints ────────────────────────────────────────────

/**
 * "Start N consent calls": a manual run of the clinic's program controller for
 * CONSENT (same classification, caps and booking as the tick). Labelled
 * `manual` when the program is disabled (D11); a plan only in shadow.
 */
export function initiateUnconsented(orgId: string, limit: number) {
  return apiCall<RunSummary>({
    method: "POST",
    url: `/apcm-workflow/initiate-unconsented`,
    params: { organization_id: orgId, number_patients_limit: limit },
  })
}

/** "Start N check-in calls": the same manual run for CHECKIN. */
export function initiateConsentedCheckin(orgId: string, limit: number) {
  return apiCall<RunSummary>({
    method: "POST",
    url: `/apcm-workflow/initiate-consented-checkin`,
    params: { organization_id: orgId, number_patients_limit: limit },
  })
}

export function generateCarePlans(orgId: string) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/generate-care-plans`,
    params: { organization_id: orgId },
  })
}

export function uploadCarePlans(orgId: string) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/upload-care-plans`,
    params: { organization_id: orgId },
  })
}

export function approveCarePlans(orgId: string, carePlanIds: string[]) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/care-plans/approve`,
    data: { organization_id: orgId, care_plan_ids: carePlanIds },
  })
}

export function rejectCarePlans(orgId: string, carePlanIds: string[]) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/care-plans/reject`,
    data: { organization_id: orgId, care_plan_ids: carePlanIds },
  })
}

export function runBatch(orgId: string) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/run-batch`,
    params: { organization_id: orgId },
  })
}

export function bulkUpdateStates(data: BulkStateUpdateRequest) {
  return apiCall<BulkUpdateResult>({
    method: "POST",
    url: `/patient-apcm-state/bulk-update`,
    data,
  })
}

export interface TphmEligibilityResponse {
  message: string
  workflow_id: string
  task_run_id: string
  patient_count: number
  csv_s3_path: string
}

export function submitTphmEligibilityByDate(orgId: string, startDate?: string, endDate?: string) {
  return apiCall<TphmEligibilityResponse>({
    method: "POST",
    url: `/stagehand/tphm-eligibility-by-date`,
    data: {
      organization_id: orgId,
      ...(startDate ? { start_date: startDate } : {}),
      ...(endDate ? { end_date: endDate } : {}),
    },
  })
}

export interface EnrollFromCSVResult {
  total_csv_rows: number
  patients_created: number
  patients_skipped: number
  patients_enrolled: number
  enrollment_errors: Array<{ patient_id: string; error: string }>
  consent_initiated: number
  skipped_patient_ids: string[]
}

export async function enrollFromCSV(
  orgId: string,
  file: File,
  initiateConsent: boolean = false,
  consentLimit?: number,
): Promise<EnrollFromCSVResult> {
  const authHeaders = await buildAuthHeaders()

  const formData = new FormData()
  formData.append("file", file)

  const params = new URLSearchParams({
    organization_id: orgId,
    initiate_consent: String(initiateConsent),
  })
  if (consentLimit) params.set("consent_limit", String(consentLimit))

  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}/apcm-workflow/enroll-from-csv?${params}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...authHeaders,
      },
    }
  )
  return response.data
}

export interface APCMStatsResult {
  patients: number
  apcm_consented_patients: number
  approved_plans_of_care: number
  /** D4: distinct patients with a completed check-in this Central month. */
  checkins_this_month: number
  consented_by_state: number
  approved_by_state: number
  checkins_completed_by_state: number
  total_calls: number
  pipeline_summary: Record<string, number>
}

export function getAPCMStats(orgId: string) {
  return apiCall<APCMStatsResult>({
    method: "GET",
    url: `/organization/${orgId}/apcm-stats`,
  })
}

export interface ChartUploadResult {
  matched: number
  unmatched: number
  updated_existing: number
  created_new: number
  unmatched_files: string[]
  errors: string[]
}

export async function uploadPatientCharts(
  orgId: string,
  files: File[],
): Promise<ChartUploadResult> {
  const authHeaders = await buildAuthHeaders()

  const formData = new FormData()
  for (const file of files) {
    formData.append("files", file)
  }

  const params = new URLSearchParams({ organization_id: orgId })

  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}/apcm-workflow/upload-patient-charts?${params}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...authHeaders,
      },
    },
  )
  return response.data
}

// ─── Deployment endpoints ───────────────────────────────────────

export function getPreflightCheck(orgId: string) {
  return apiCall<PreflightCheck>({
    method: "GET",
    url: `/apcm-workflow/preflight-check`,
    params: { organization_id: orgId },
  })
}

export function initializeAllPatients(orgId: string, dryRun: boolean = false) {
  return apiCall<BulkInitResult>({
    method: "POST",
    url: `/apcm-workflow/initialize-all-patients`,
    params: { organization_id: orgId, dry_run: dryRun },
  })
}

// --- Approved PCPs ---

export function getApprovedPCPs(orgId: string) {
  return apiCall<{ approved_pcps: string[] }>({
    method: "GET",
    url: `/apcm-workflow/org/${orgId}/approved-pcps`,
  })
}

export function updateApprovedPCPs(orgId: string, pcps: string[]) {
  return apiCall<{ approved_pcps: string[] }>({
    method: "PUT",
    url: `/apcm-workflow/org/${orgId}/approved-pcps`,
    data: { approved_pcps: pcps },
  })
}

// --- Consent CSV Upload ---

export interface ConsentCSVResult {
  matched: number
  unmatched: number
  already_consented: number
  newly_consented: number
  unmatched_mrns: string[]
}

export async function uploadConsentedCSV(
  orgId: string,
  file: File,
): Promise<ConsentCSVResult> {
  const authHeaders = await buildAuthHeaders()

  const formData = new FormData()
  formData.append("file", file)

  const params = new URLSearchParams({ organization_id: orgId })

  const response = await axios.post(
    `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}/apcm-workflow/upload-consented-csv?${params}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
        ...authHeaders,
      },
    },
  )
  return response.data
}

// ─── Care Plan Generation Progress ──────────────────────────────

export interface CarePlanGenerationStatus {
  is_active: boolean
  generating: number
  completed: number
  failed: number
  total: number
  state_counts: Record<string, number>
  sqs: {
    messages_available: number
    messages_in_flight: number
    messages_delayed: number
  }
  patients_generating: Array<{
    patient_id: string
    patient_name: string
    since: string | null
  }>
  patients_failed: Array<{
    patient_id: string
    patient_name: string
    since: string | null
  }>
}

export function getCarePlanGenerationStatus(orgId: string) {
  return apiCall<CarePlanGenerationStatus>({
    method: "GET",
    url: `/apcm-workflow/care-plan-generation-status`,
    params: { organization_id: orgId },
  })
}

// ─── Bulk Ingestion from Drive ──────────────────────────────────

export function ingestSimplifiedFromDrive(
  orgId: string,
  folderId: string,
  options?: { recursive?: boolean; skipDuplicate?: boolean; maxConcurrent?: number },
) {
  return apiCall<{ job_id: string; status: string; message: string }>({
    method: "POST",
    url: `/apcm-workflow/ingest-simplified-from-drive`,
    data: {
      organization_id: orgId,
      folder_id: folderId,
      recursive: options?.recursive ?? false,
      skip_duplicate: options?.skipDuplicate ?? false,
      max_concurrent: options?.maxConcurrent ?? 5,
    },
  })
}

export function getBulkIngestionJob(jobId: string) {
  return apiCall<BulkIngestionJob>({
    method: "GET",
    url: `/apcm-workflow/bulk-ingestion-job/${jobId}`,
  })
}

export interface APCMBillingCodeEntry {
  patient_id: string
  first_name: string
  last_name: string
  mrn?: string | null
  fhir_id?: string | null
  date_of_birth?: string | null
  consent_date?: string | null
  date_of_service?: string | null // YYYY-MM-DD, computed server-side in clinic time
  pcp_name?: string | null
  icd_codes: string[]
  g_code: string
  month_of_service?: string | null
  classified_at?: string | null
}

export interface APCMBillingCodesResponse {
  organization_id: string
  month_of_service: string
  total_classifications: number
  g_code_counts: Record<string, number>
  entries: APCMBillingCodeEntry[]
}

export function getAPCMBillingCodes(orgId: string, month: string) {
  return apiCall<APCMBillingCodesResponse>({
    method: "GET",
    url: `/organization/${orgId}/apcm-billing-codes`,
    params: { month },
  })
}

export type DisenrollReason =
  | "deceased"
  | "moved"
  | "patient_request"
  | "lost_to_follow_up"
  | "insurance_changed"
  | "other"

export interface DisenrollPayload {
  organization_id: string
  reason: DisenrollReason
  deceased_date?: string
  note?: string
}

export function disenrollPatient(patientId: string, payload: DisenrollPayload) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/patient/${patientId}/disenroll`,
    data: payload,
  })
}

export interface ReenrollPayload {
  organization_id: string
  note?: string
}

export function reenrollPatient(patientId: string, payload: ReenrollPayload) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/patient/${patientId}/reenroll`,
    data: payload,
  })
}

// ─── Pipeline gaps ─────────────────────────────────────────────────────

export interface StuckStateBucket {
  count: number
  oldest_age_hours: number | null
}

export interface PipelineGaps {
  patients_without_state: number
  stuck_per_state: {
    CONSENT_SCHEDULED: StuckStateBucket
    CARE_PLAN_GENERATING: StuckStateBucket
    CARE_PLAN_UPLOADING: StuckStateBucket
    CHECK_IN_SCHEDULED: StuckStateBucket
  }
  consented_missing_chart_data: {
    total: number
    by_state: Record<string, number>
  }
  last_runs: {
    billing_backfill_ran_at: string | null
    billing_backfill_latest_month: string | null
  }
}

export function getPipelineGaps(orgId: string) {
  return apiCall<PipelineGaps>({
    method: "GET",
    url: `/apcm-workflow/pipeline-gaps`,
    params: { organization_id: orgId },
  })
}

// ─── Per-patient blockers ──────────────────────────────────────────────

export interface PatientBlocker {
  check:
    | "consent"
    | "phone_number"
    | "chart_data"
    | "fhir_id"
    | "care_plan"
    | "pcp"
  label: string
  passed: boolean | null
  detail: string
}

export interface PatientBlockersResponse {
  patient_id: string
  current_state: string | null
  blockers: PatientBlocker[]
  next_action: string
}

export function getPatientBlockers(patientId: string, orgId: string) {
  return apiCall<PatientBlockersResponse>({
    method: "GET",
    url: `/apcm-workflow/patient/${patientId}/apcm-blockers`,
    params: { organization_id: orgId },
  })
}

export function backfillBillingClassifications(orgId?: string) {
  return apiCall<any>({
    method: "POST",
    url: `/apcm-workflow/backfill-billing-classifications`,
    params: orgId ? { organization_id: orgId } : undefined,
  })
}

export function getBulkIngestionJobs(orgId: string) {
  return apiCall<BulkIngestionJobSummary[]>({
    method: "GET",
    url: `/apcm-workflow/bulk-ingestion-jobs`,
    params: { organization_id: orgId },
  })
}
